import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  PDA1010_R01Card,
  PDA1010_R01DetailDto,
  PDA1010_R01MeasurementType,
} from "./PDA1010_R01Dto";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

interface PDA1010_R01ChartProps {
  card: PDA1010_R01Card;
  detailList: PDA1010_R01DetailDto[];
  isLoading: boolean;
}

/** 측정 항목에 대응하는 차트 단위를 반환합니다. */
const getPDA1010_R01Unit = (
  measurementType: PDA1010_R01MeasurementType
) => (measurementType === "온도" ? "℃" : "%");

/** 온도와 그 외 측정 항목을 구분하는 차트 색상을 반환합니다. */
const getPDA1010_R01Color = (
  measurementType: PDA1010_R01MeasurementType
) => (measurementType === "온도" ? "#f47b20" : "#2563eb");

/**
 * 상세 로그가 과도하게 표시되지 않도록 시간별 마지막 측정값 한 건만 남깁니다.
 * 이 함수에는 측정 항목별로 필터링된 목록이 전달됩니다.
 */
const getPDA1010_R01HourlyDetailList = (
  detailList: PDA1010_R01DetailDto[]
) => {
  const hourlyDetailMap = new Map<string, PDA1010_R01DetailDto>();

  detailList.forEach((detail) => {
    const hourKey = detail.createdatetime.slice(0, 13);
    hourlyDetailMap.set(hourKey, detail);
  });

  return Array.from(hourlyDetailMap.values()).sort((left, right) =>
    left.createdatetime.localeCompare(right.createdatetime)
  );
};

/** 모든 측정 항목 차트에 공통으로 적용할 축·툴팁 옵션을 생성합니다. */
const createChartOptions = (unit: string): ChartOptions<"line"> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context) =>
          context.parsed.y === null
            ? ""
            : `${context.parsed.y.toLocaleString()} ${unit}`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
    },
    y: {
      beginAtZero: false,
      ticks: {
        callback: (value) =>
          `${Number(value).toLocaleString(undefined, {
            maximumFractionDigits: 1,
          })}${unit}`,
      },
    },
  },
});

/** 원본 로그값에 소수 자릿수를 적용해 Chart.js 데이터로 변환합니다. */
const createChartData = (
  detailList: PDA1010_R01DetailDto[],
  color: string
): ChartData<"line"> => {
  const hourlyDetailList = getPDA1010_R01HourlyDetailList(detailList);

  return {
    labels: hourlyDetailList.map((detail) =>
      detail.createdatetime.slice(11, 16)
    ),
    datasets: [
      {
        data: hourlyDetailList.map((detail) => {
        if (detail.value === null || detail.value.trim() === "") {
          return null;
        }
        const rawValue = Number(detail.value);
        return Number.isFinite(rawValue)
          ? rawValue / Math.pow(10, detail.decimalPlaces)
          : null;
        }),
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.2,
        spanGaps: true,
      },
    ],
  };
};

/** 선택한 카드의 측정 항목별 최근 24시간 추이를 표시합니다. */
const PDA1010_R01Chart = ({
  card,
  detailList,
  isLoading,
}: PDA1010_R01ChartProps) => {
  const measurementTypeList = card.measurementList.map(
    (measurement) => measurement.measurementType
  );
  const startDate = detailList[0]?.createdatetime.slice(0, 10);
  const endDate = detailList[detailList.length - 1]?.createdatetime.slice(0, 10);
  const measuredDate =
    startDate && endDate && startDate !== endDate
      ? `${startDate} ~ ${endDate}`
      : startDate ?? "-";

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-white px-4 py-5">
      <div className="text-[17px] font-bold">{card.cardName}</div>
      <div className="mt-3 text-[15px]">
        일자 : <span className="font-semibold">{measuredDate}</span>
      </div>

      {isLoading && detailList.length === 0 && (
        <div className="mt-8 text-center text-gray-500">조회 중입니다.</div>
      )}

      {!isLoading && detailList.length === 0 && (
        <div className="mt-8 text-center text-gray-500">
          조회된 이력이 없습니다.
        </div>
      )}

      {measurementTypeList.map((measurementType) => {
        const measurementDetailList = detailList.filter(
          (detail) => detail.measurementType === measurementType
        );

        if (measurementDetailList.length === 0) {
          return null;
        }

        const unit = getPDA1010_R01Unit(measurementType);

        return (
          <section key={measurementType} className="mt-5">
            <h2 className="mb-2 text-[16px] font-semibold">
              {measurementType} 추이
            </h2>
            <div className="h-[180px] border border-gray-300 p-2">
              <Line
                data={createChartData(
                  measurementDetailList,
                  getPDA1010_R01Color(measurementType)
                )}
                options={createChartOptions(unit)}
              />
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default PDA1010_R01Chart;
